(() => {
  if (customElements.get('rc-product-tabs')) return;

  class RoyalProductTabs extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.events = new AbortController();
      const { signal } = this.events;
      this.triggers = [...this.querySelectorAll('[data-tab-trigger]')];
      this.panels = [...this.querySelectorAll('[data-tab-panel]')];
      this.infograms = this.infograms || [];
      this.panels.forEach(panel => this.mountContent(panel));

      this.triggers.forEach((trigger, index) => {
        trigger.addEventListener('click', () => this.activate(trigger), { signal });
        trigger.addEventListener('keydown', event => {
          let next;
          if (event.key === 'ArrowRight') next = (index + 1) % this.triggers.length;
          if (event.key === 'ArrowLeft') next = (index + this.triggers.length - 1) % this.triggers.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = this.triggers.length - 1;
          if (next === undefined) return;
          event.preventDefault();
          this.activate(this.triggers[next]);
          this.triggers[next].focus();
        }, { signal });
      });

      window.addEventListener('message', event => {
        if (event.origin !== 'https://e.infogram.com' || typeof event.data !== 'string') return;
        const [message, value] = event.data.split(':');
        if (!message.startsWith('iframeHeight')) return;
        const ids = message.slice('iframeHeight'.length).split('#');
        const height = Number(value);
        if (!Number.isFinite(height) || height <= 0) return;
        this.infograms.forEach(({ frame, id }) => {
          if (event.source === frame.contentWindow && ids.includes(id)) {
            frame.style.height = `${height}px`;
            frame.contentWindow.postMessage(`iframeHeightReceived:${height}`, 'https://e.infogram.com');
          }
        });
      }, { signal });

      this.resizeObserver = new ResizeObserver(() => this.resizeInfograms());
      this.resizeObserver.observe(this);
      const selected = this.triggers.find(trigger => trigger.getAttribute('aria-selected') === 'true') || this.triggers[0];
      if (selected) this.activate(selected);
    }

    disconnectedCallback() {
      this.events?.abort();
      this.events = null;
      this.resizeObserver?.disconnect();
    }

    activate(trigger) {
      const target = trigger.dataset.tabTrigger;
      this.triggers.forEach(button => {
        const active = button === trigger;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-selected', String(active));
        button.tabIndex = active ? 0 : -1;
      });
      this.panels.forEach(panel => {
        const active = panel.dataset.tabPanel === target;
        panel.classList.toggle('is-active', active);
        panel.hidden = !active;
        if (active) this.loadInfograms(panel);
      });
      this.resizeInfograms();
    }

    mountContent(panel) {
      const template = panel.querySelector('template[data-crown-tab-content]');
      if (!template) return;
      const content = template.content.cloneNode(true);

      // Woo stored Infogram embeds as scripts. Use the provider's iframe contract
      // so graphic IDs remain intact without duplicate script IDs or legacy JS.
      content.querySelectorAll('script').forEach(script => {
        const match = /^(infogram(?:img)?)_([0-9]+)_([\w\-]{16,}|_\/[\w\-]{20,})$/i.exec(script.id);
        let source;
        try { source = new URL(script.getAttribute('src')); } catch (_) { /* Inline script. */ }
        if (!match || !source || source.origin !== 'https://e.infogram.com' || source.pathname !== '/js/dist/embed.js') {
          script.remove();
          return;
        }

        this.mountInfogram(script, panel, {
          id: match[3], title: script.title,
          type: match[1].toLowerCase() === 'infogramimg' ? 'image' : 'interactive',
        });
      });
      content.querySelectorAll('iframe[src]').forEach(original => {
        let source;
        try { source = new URL(original.getAttribute('src')); } catch (_) { return; }
        const match = /^\/([\w\-]{16,}|_\/[\w\-]{20,})\/?$/.exec(source.pathname);
        if (source.origin !== 'https://e.infogram.com' || !match) return;

        // Native responsive embeds do not send height updates. Preserve their
        // provider flags and move the exported aspect ratio onto the iframe.
        const wrapper = original.parentElement;
        let aspectRatio;
        if (source.searchParams.get('embed_type') === 'responsive_iframe'
          && wrapper?.tagName === 'DIV' && wrapper.style.position === 'relative'
          && wrapper.style.height === '0px' && original.style.position === 'absolute') {
          const padding = wrapper.style.paddingTop;
          const percent = Number.parseFloat(padding);
          if (padding.endsWith('%') && percent > 0) {
            aspectRatio = `100 / ${percent}`;
            ['position', 'width', 'height', 'padding', 'overflow', 'will-change'].forEach(property => {
              wrapper.style.removeProperty(property);
            });
          }
        }
        this.mountInfogram(original, panel, {
          id: match[1], title: original.title, sourceUrl: source.href, aspectRatio,
        });
      });
      // Newer Infogram exports use a data-id div and one shared inline loader.
      // Convert the div directly after removing scripts; never execute that loader.
      content.querySelectorAll('.infogram-embed[data-id]').forEach(embed => {
        if (!/^([\w\-]{16,}|_\/[\w\-]{20,})$/.test(embed.dataset.id)) return;
        this.mountInfogram(embed, panel, {
          id: embed.dataset.id, title: embed.dataset.title,
          type: embed.dataset.type, page: embed.dataset.page,
        });
      });
      // The product title owns H1; keep imported article headings below it.
      content.querySelectorAll('h1').forEach(original => {
        const heading = document.createElement('h2');
        [...original.attributes].forEach(attribute => {
          heading.setAttribute(attribute.name, attribute.name === 'aria-level' ? '2' : attribute.value);
        });
        heading.append(...original.childNodes);
        original.replaceWith(heading);
      });
      this.wrapParagraphs(content);
      this.arrangeContent(content);
      template.replaceWith(content);
    }

    arrangeContent(content) {
      const illustrations = [...content.querySelectorAll('iframe, img, video')];
      if (!illustrations.length) return;

      const layout = document.createElement('div');
      layout.className = 'rc-product-tabs__analysis-layout';
      const copy = document.createElement('div');
      copy.className = 'rc-product-tabs__analysis-copy';
      const media = document.createElement('div');
      media.className = 'rc-product-tabs__analysis-media';

      illustrations.forEach(illustration => {
        if (media.contains(illustration)) return;
        // Keep linked images and figure captions attached to their illustration.
        // Mixed chart wrappers stay in the copy column so no prose is lost.
        const item = illustration.closest('figure')
          || illustration.closest('a')
          || illustration.closest('picture')
          || illustration;
        let parent = item.parentElement;
        media.append(item);
        while (parent && !parent.textContent.trim() && !parent.querySelector('img, iframe, video, table, hr, audio')) {
          const ancestor = parent.parentElement;
          parent.remove();
          parent = ancestor;
        }
      });

      if (content.textContent.trim()) {
        copy.append(...content.childNodes);
        layout.append(copy, media);
      } else {
        layout.classList.add('rc-product-tabs__analysis-layout--media-only');
        layout.append(media);
      }
      content.replaceChildren(layout);
    }

    mountInfogram(original, panel, { id, title, type, page, sourceUrl, aspectRatio }) {
      const url = new URL(sourceUrl || `https://e.infogram.com/${id}`);
      url.searchParams.set('parent_url', window.location.href);
      if (!url.searchParams.has('src')) url.searchParams.set('src', 'embed');
      if (type === 'image') url.searchParams.set('type', 'image');
      const pageNumber = Number.parseInt(page, 10);
      if (pageNumber > 1) url.searchParams.set('page', pageNumber);
      if (!url.hash) url.hash = 'async_embed';
      const frame = document.createElement('iframe');
      frame.className = 'rc-product-tabs__infogram';
      frame.title = title || 'Coffee analysis chart';
      frame.loading = 'lazy';
      if (aspectRatio) {
        frame.style.aspectRatio = aspectRatio;
        frame.style.height = 'auto';
      }
      frame.setAttribute('allowfullscreen', '');
      frame.setAttribute('scrolling', 'no');
      frame.addEventListener('load', () => this.resizeInfograms());
      this.infograms.push({ frame, id, panel, url: url.href, loaded: false });
      original.replaceWith(frame);
    }

    wrapParagraphs(content) {
      // ACF's WYSIWYG output uses wpautop. Restore the exported blank-line
      // boundaries around inline passages while leaving existing blocks intact.
      const blocks = new Set(['ADDRESS', 'ARTICLE', 'ASIDE', 'BLOCKQUOTE', 'DETAILS', 'DIV',
        'DL', 'FIELDSET', 'FIGCAPTION', 'FIGURE', 'FOOTER', 'FORM', 'H1', 'H2', 'H3', 'H4',
        'H5', 'H6', 'HEADER', 'HR', 'IFRAME', 'MAIN', 'NAV', 'OL', 'P', 'PRE', 'SECTION',
        'TABLE', 'UL', 'VIDEO']);
      const output = document.createDocumentFragment();
      let paragraph;
      const flush = () => {
        if (paragraph) output.append(paragraph);
        paragraph = null;
      };
      const append = node => {
        paragraph = paragraph || document.createElement('p');
        paragraph.append(node);
      };

      [...content.childNodes].forEach(node => {
        if (node.nodeType === 3) {
          node.textContent.split(/(\r?\n[ \t]*\r?\n(?:[ \t]*\r?\n)*)/).forEach((part, index) => {
            if (index % 2) flush();
            else if (part.trim() || paragraph) append(document.createTextNode(part));
          });
        } else if (blocks.has(node.tagName)) {
          flush();
          output.append(node);
        } else {
          append(node);
        }
      });
      flush();
      content.replaceChildren(output);
    }

    loadInfograms(panel) {
      this.infograms.forEach(embed => {
        if (embed.panel === panel && !embed.loaded) {
          embed.loaded = true;
          embed.frame.src = embed.url;
        }
      });
    }

    resizeInfograms() {
      this.infograms.forEach(({ frame, loaded }) => {
        const width = frame.clientWidth;
        if (loaded && width > 0 && frame.contentWindow) {
          frame.contentWindow.postMessage(`iframeWidth:${width}`, 'https://e.infogram.com');
        }
      });
    }
  }

  customElements.define('rc-product-tabs', RoyalProductTabs);
})();
